"use client";

import { useCurrentUser } from "@zapier/client";
import { useNamespacedTranslations } from "../useNamespacedTranslations";
import {
  useMultiValueParameter,
  type MultiFilterOperator,
} from "../../../../hooks/useRouterParams";
import { OwnerFilterMenu, useOwnerNameResolver } from "../../OwnerFilterMenu";
import type { ActiveChip } from "../types";
import { useRegisterFilter } from "../FilterBarContext";
import type { FilterControllerProps } from "../types";

const ICON = "personCircle";

interface Props extends FilterControllerProps {
  /** Whether teams can be selected as an owner. Defaults to `true`. Not part
   * of `FilterControllerProps` since only some callers (e.g. Connections)
   * need to override it. */
  allowTeams?: boolean;
}

export function OwnerFilterController({
  isOpen,
  namespace,
  onInteraction,
  allowTeams,
}: Props): JSX.Element | null {
  const t = useNamespacedTranslations(namespace);

  const owners = useMultiValueParameter("owners", "ownersOperator");
  const { user } = useCurrentUser();
  const resolveOwnerName = useOwnerNameResolver(owners.values, {
    meLabel: t("owner.me"),
    meId: user ? String(user.id) : undefined,
  });

  const hasValue = owners.values.length > 0;
  const valuesKey = owners.values.join(",");

  let chip: ActiveChip | undefined;
  let signature = "owner:none";

  if (hasValue) {
    const first = resolveOwnerName(owners.values[0]);
    const count = owners.values.length - 1;
    const isNot = owners.operator === "none";
    const label = t("owner.chipLabel", {
      first,
      count,
      operator: owners.operator,
    });
    const ariaLabel = t(`owner.chipAriaLabel.${isNot ? "isNot" : "anyOf"}`, {
      first,
      count,
    });
    const tooltip =
      owners.values.length > 1
        ? owners.values.map((id) => resolveOwnerName(id)).join(", ")
        : undefined;
    chip = {
      key: "owner",
      iconName: ICON,
      title: t("owner.label"),
      label,
      ariaLabel,
      tooltip,
    };
    // label captures resolved names, so the chip re-renders when an async
    // name lookup resolves id -> display name.
    signature = `owner:${valuesKey}:${owners.operator}:${label}`;
  } else if (isOpen) {
    chip = {
      key: "owner",
      iconName: ICON,
      title: t("owner.label"),
      ariaLabel: t("owner.label"),
      isDraft: true,
    };
    signature = "owner:draft";
  }

  useRegisterFilter({
    type: "owner",
    hasValue,
    signature,
    chip,
    renderMenu: () => (
      <OwnerFilterMenu
        namespace={namespace}
        selectedIds={owners.values}
        operator={owners.operator}
        allowTeams={allowTeams}
        onOperatorChange={(op: MultiFilterOperator) => owners.setOperator(op)}
        onToggle={(id: string, pendingOperator?: MultiFilterOperator) => {
          owners.toggle(id, pendingOperator);
          onInteraction?.();
        }}
      />
    ),
  });
  // eslint-disable-next-line unicorn/no-null -- headless controller renders nothing
  return null;
}
